import { NestedComplexListItemProps } from "@inductiveautomation/ws-components";
import { useMemo } from "react";
import { OrgNodeTree } from "../api/organizations.api";
import { orgNodeTreeToListItem } from "../Organizations.data";
import { DirectMemberChip } from "./DirectMemberChip";

const collectDescendantOrgIds = (node: OrgNodeTree): string[] =>
  node.children.flatMap((child) => [
    child.orgId,
    ...collectDescendantOrgIds(child),
  ]);

/**
 * True when the current account is a direct member of EVERY descendant org under
 * the root (so leaving the root revokes no inherited access).
 * False when the root has no descendants — there is nothing to be unaffected.
 */
export const useAllDescendantsDirectMembers = (
  orgStructure: OrgNodeTree | undefined,
  joinedOrgs: string[] | undefined
): boolean =>
  useMemo(() => {
    if (!orgStructure) return false;
    const descendants = collectDescendantOrgIds(orgStructure);
    if (descendants.length === 0) return false;
    const joined = new Set(joinedOrgs ?? []);
    return descendants.every((orgId) => joined.has(orgId));
  }, [orgStructure, joinedOrgs]);

/**
 * Builds the org-tree list items for the leave dialog, tagging every descendant org
 * the current account is a direct member of with a "Direct member" chip. The root org
 * (the one being left) is never tagged — access to it IS affected.
 */
export const useDirectMemberOrgItems = (
  orgStructure: OrgNodeTree | undefined,
  joinedOrgs: string[] | undefined,
  testIdPrefix: string
): NestedComplexListItemProps[] =>
  useMemo(() => {
    if (!orgStructure) return [];
    const directMemberOrgIds = new Set(
      (joinedOrgs ?? []).filter((orgId) => orgId !== orgStructure.orgId)
    );
    return [
      orgNodeTreeToListItem(orgStructure, testIdPrefix, "default", {
        directMemberOrgIds,
        directMemberChip: <DirectMemberChip />,
      }),
    ];
  }, [orgStructure, joinedOrgs, testIdPrefix]);
